import React, { Suspense, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FolderKanban,
  RadioTower,
  Network,
  FlaskConical,
  FileText,
  Users,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Sun,
  Bell,
  Globe2,
  Menu,
  X,
  LogOut,
} from "lucide-react";
import { useLab } from "@/context/LabContext";
import { useLabAuth } from "@/pages/Lab/LabAuth";
import { NexisLogo } from "@/components/NexisLogo";

const NAV = [
  { to: "/lab", end: true, key: "nav.dashboard", icon: LayoutDashboard },
  { to: "/lab/projects", key: "nav.projects", icon: FolderKanban },
  { to: "/lab/signal", key: "nav.signal", icon: RadioTower },
  { to: "/lab/network", key: "nav.network", icon: Network },
  { to: "/lab/experiments", key: "nav.experiments", icon: FlaskConical },
  { to: "/lab/energy", key: "nav.energy", icon: Sun },
  { to: "/lab/reports", key: "nav.reports", icon: FileText },
  { to: "/lab/team", key: "nav.team", icon: Users, adminOnly: true },
  { to: "/lab/history", key: "nav.history", icon: HistoryIcon },
  { to: "/lab/settings", key: "nav.settings", icon: SettingsIcon },
];

function userInitials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function LangSwitch() {
  const { language, setLanguage } = useLab();
  return (
    <div className="flex items-center rounded-md overflow-hidden border border-white/15" data-testid="lab-lang-switch">
      {["pt", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLanguage(l)}
          data-testid={`lab-lang-${l}`}
          className={`px-2.5 py-1 text-xs font-semibold tracking-wide transition-colors ${
            language === l ? "bg-cyan-400 text-[#0E2340]" : "text-slate-300 hover:bg-white/10"
          }`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function LabFallback() {
  const { t } = useLab();
  return (
    <div className="flex items-center justify-center py-24 text-slate-400 text-sm" data-testid="lab-loading">
      <span className="w-4 h-4 mr-2 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin" />
      {t("common.loading")}
    </div>
  );
}

export default function LabLayout() {
  const { t } = useLab();
  const { user: authUser, logout } = useLabAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const current =
    NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to) && n.to !== "/lab")) || NAV[0];

  return (
    <div className="min-h-screen bg-[#EEF2F7] text-[#0F2742]">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="lab-drawer-backdrop"
        />
      )}

      {/* Sidebar / Drawer */}
      <aside
        className={`w-60 bg-[#0E2340] text-slate-200 flex flex-col fixed inset-y-0 left-0 z-40 transform transition-transform duration-200 lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="lab-sidebar"
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          <NexisLogo subtitle={t("app.subtitle")} />
          <button className="lg:hidden text-slate-300 hover:text-white" onClick={() => setMobileOpen(false)} data-testid="lab-drawer-close" aria-label="Close menu">
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.filter((item) => !item.adminOnly || authUser?.role === "admin").map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMobileOpen(false)}
                data-testid={`lab-nav-${item.key.split(".")[1]}`}
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-all ${
                    isActive
                      ? "bg-cyan-400/15 text-white border-l-2 border-cyan-400 pl-[10px]"
                      : "text-slate-300 hover:bg-white/5 hover:text-white border-l-2 border-transparent"
                  }`
                }
              >
                <Icon size={18} className="shrink-0" />
                <span>{t(item.key)}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="m-3 rounded-xl overflow-hidden relative bg-gradient-to-br from-[#0b4a6b] via-[#0e6d8c] to-[#0f766e] p-4">
          <Globe2 className="absolute -right-3 -bottom-3 text-white/10" size={88} />
          <div className="relative">
            <div className="text-sm font-semibold text-white">{t("footer.region")}</div>
            <div className="text-[11px] text-cyan-100/80">{t("footer.region_sub")}</div>
          </div>
        </div>
      </aside>

      {/* Main column */}
      <div className="lg:ml-60 flex flex-col min-h-screen">
        <header className="h-16 bg-[#0E2340] text-white flex items-center justify-between px-3 sm:px-6 sticky top-0 z-20 border-b border-white/10">
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="lg:hidden text-slate-200 hover:text-white shrink-0"
              onClick={() => setMobileOpen(true)}
              data-testid="lab-hamburger"
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
            <div className="min-w-0">
              <h1 className="text-sm sm:text-base font-bold leading-none truncate">{t(current.key)}</h1>
              <p className="hidden sm:block text-[11px] text-slate-400 mt-1">{t("header.actions")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="hidden md:inline text-sm font-semibold tracking-wide">
              BSGOTECH <span className="text-cyan-400">×</span> TecNon
            </span>
            <LangSwitch />
            <button className="relative text-slate-300 hover:text-white transition-colors hidden sm:block" data-testid="lab-bell">
              <Bell size={18} />
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-cyan-400" />
            </button>
            <div className="flex items-center gap-2 sm:pl-3 sm:border-l border-white/10">
              <div className="w-8 h-8 rounded-full bg-cyan-400/20 border border-cyan-400/40 flex items-center justify-center text-cyan-300 text-xs font-bold overflow-hidden" data-testid="lab-user-avatar">
                {authUser?.photo
                  ? <img src={authUser.photo} alt="" className="w-full h-full object-cover" />
                  : userInitials(authUser?.name)}
              </div>
              <div className="hidden md:block leading-tight">
                <div className="text-xs font-semibold" data-testid="lab-user-name">{authUser?.name || "—"}</div>
                <div className="text-[10px] text-slate-400" data-testid="lab-user-role">{authUser?.role === "admin" ? t("prof.role_admin_full") : (authUser?.team || "TecNon")}</div>
              </div>
              <button data-testid="lab-logout" onClick={async () => { await logout(); navigate("/lab/login"); }} title={t("auth.logout")}
                className="ml-1 text-slate-300 hover:text-white transition-colors"><LogOut size={18} /></button>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 overflow-x-hidden" data-testid="lab-main">
          <Suspense fallback={<LabFallback />}>
            <Outlet />
          </Suspense>
        </main>

        <footer className="px-4 sm:px-6 py-3 text-[11px] text-slate-500 border-t border-slate-200 bg-white/60">
          {t("sim.disclaimer")} · NEXIS Virtual Lab V0.1 · BSGOTECH × TecNon
        </footer>
      </div>
    </div>
  );
}
